import { useEffect, useRef } from 'react';
import starkString from 'starkstring';

/**
 * The one-time code read straight from the SMS while the code step is open (WebOTP): Chrome on Android
 * hands it over once the person taps «اجازه», but only when the SMS ends with the line
 * `@<this page's host> #<code>`. Elsewhere, or with any other SMS, nothing happens and the code is typed
 * as before (Safari on iOS offers it above the keyboard through `autoComplete="one-time-code"`).
 * The read stops when the component leaves.
 */
const useSmsCode = (onCode: (code: string) => void, length: number) => {
  const latestOnCode = useRef(onCode);
  latestOnCode.current = onCode;

  useEffect(() => {
    if (typeof window === 'undefined' || !('OTPCredential' in window)) return undefined;

    const reader = new AbortController();
    // lib.dom has no `otp` member yet.
    const request = { otp: { transport: ['sms'] }, signal: reader.signal } as Parameters<
      typeof navigator.credentials.get
    >[0];

    navigator.credentials
      .get(request)
      .then((credential) => {
        const digits = starkString((credential as { code?: string } | null)?.code ?? '')
          .englishNumber()
          .toString()
          .replace(/\D/g, '');
        if (digits.length === length) latestOnCode.current(digits);
      })
      .catch(() => {
        // Declined, timed out or stopped on leaving: the code is typed instead.
      });

    return () => reader.abort();
  }, [length]);
};

export default useSmsCode;
